/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { IActionContext } from '@msdyn365-commerce/core';

export interface IUomProductInventoryInput {
    productId: number;
    unitOfMeasure: string;
}

export interface IUomProductFactor {
    productId: number;
    factor: number;
}
/**
 * GetInventoryUom Input Action
 */
export class GetInventoryUomInput implements Msdyn365.IActionInput {
    public products: IUomProductInventoryInput[];
    public multiUomProducts: IUomProductFactor[];
    constructor(products: IUomProductInventoryInput[], multiUomProducts: IUomProductFactor[]) {
        this.products = products;
        this.multiUomProducts = multiUomProducts;
    }
    public getCacheKey = () => `UNITOFMEASUREINVENTORY`;
    public getCacheObjectType = () => 'UNITOFMEASUREINVENTORY';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetInventoryUomData {
    productInventoryInformation: IProductInventoryInformation[];
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetInventoryUomInput([], []);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetInventoryUomInput, ctx: Msdyn365.IActionContext): Promise<IGetInventoryUomData> {
    const multiUomProductIds = input.multiUomProducts.map(p => p.productId);
    const csProducts = input.products.filter(p => p.unitOfMeasure.toUpperCase() === 'CS').map(pr => pr.productId);
    const eaProducts = input.products
        .filter(p => p.unitOfMeasure.toUpperCase() === 'EA' || multiUomProductIds.includes(p.productId))
        .map(pr => pr.productId);
    const csAvailabilities = getAvailability(csProducts, 1, 'CS', ctx);
    const eaAvailabilities = getAvailability(eaProducts, 0, 'EA', ctx);
    const allAvailabilities = [];
    allAvailabilities.push(...(await csAvailabilities));
    allAvailabilities.push(...(await eaAvailabilities));

    return { productInventoryInformation: allAvailabilities };
}

const getAvailability = async (productIds: number[], quantityUnitTypeValue: number, uom: string, ctx: IActionContext) => {
    if (!productIds.length) {
        return [];
    }
    const availabilities = await getEstimatedAvailabilityAsync(
        { callerContext: ctx },
        { ProductIds: productIds, DefaultWarehouseOnly: true, QuantityUnitTypeValue: quantityUnitTypeValue }
    );
    const availabilities2 = mapAggregatedProductInventoryInformation(ctx, availabilities);
    availabilities2.forEach(a => (a.ProductAvailableQuantity.UnitOfMeasure = uom));
    return availabilities2;
};

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetInventoryUomData>>action,
    // TODO: Give your data action a unique id
    id: 'GetInventoryUom',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
